import { current } from '@reduxjs/toolkit';
import useMediaQueries from 'app/hooks/useMediaQueries';
import React from 'react';
import { classNames } from 'react-select/dist/declarations/src/utils';
const NULL_PAGE = 0;

type PaginationType = 'previous' | 'next' | 'page';
export function PaginationButton({
  className,
  page,
  onClick,
  disabled = false,
  isActive = false,
}: {
  className?: string;
  disabled?: boolean;
  page?: React.ReactNode;
  currentPage: number;
  isActive?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`w-8 h-8 flex rounded-lg  justify-center items-center text-gray-dark focus:outline-none ${
        isActive ? 'bg-green text-white' : 'border'
      } ${className}`}
    >
      {page}
    </button>
  );
}

function getPaginationElements(
  pages: number[],
  currentPage: number,
  windowSize = 5,
) {
  if (pages.length <= windowSize + 2) {
    return pages;
  }

  if (currentPage >= 1 && currentPage <= windowSize - 1) {
    return pages.slice(0, windowSize + 2).concat(...[NULL_PAGE, pages.length]);
  }

  if (currentPage >= pages.length - windowSize + 2) {
    return [1, NULL_PAGE].concat(
      pages.slice(pages.length - windowSize - 2, pages.length),
    );
  }

  return [1, NULL_PAGE].concat(
    pages
      .slice(currentPage - windowSize + 2, currentPage + 2)
      .concat(...[NULL_PAGE, pages.length]),
  );
}

export default function BasePagination({
  per_page,
  totalCount,
  onPageChange,
  currentPage,
}: {
  per_page: number;
  totalCount: number;
  currentPage: number;
  onPageChange: (pageNumber: number) => void;
}) {
  const pages = Array.from({ length: totalCount }).map((_, index) => index + 1);
  const { md_down: isMobile } = useMediaQueries();
  const paginatedElements = getPaginationElements(pages, currentPage);

  return (
    <div className="inline-flex gap-2">
      <PaginationButton
        currentPage={currentPage}
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        page={<>{'<'}</>}
      />

      {!isMobile &&
        paginatedElements.map((page, index) => (
          <PaginationButton
            key={`${page} + ${index}`}
            currentPage={currentPage}
            page={page === NULL_PAGE ? '...' : page}
            onClick={() => onPageChange(page)}
            isActive={currentPage === page}
            disabled={currentPage === page || page === NULL_PAGE}
          />
        ))}

      {isMobile && (
        <PaginationButton
          currentPage={currentPage}
          page={currentPage}
          onClick={() => {}}
          isActive={true}
          disabled={true}
        />
      )}

      <PaginationButton
        currentPage={currentPage}
        disabled={currentPage === totalCount}
        onClick={() => onPageChange(currentPage + 1)}
        page={<>{'>'}</>}
      />
    </div>
  );
}
